import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_CONFIG } from '../../components/config/api';
import { getAccessToken } from '../../services/authService';
import { 
  getBungieIconUrl, 
  handleIconError, 
  createTextIconPlaceholder,
  FALLBACK_ICONS 
} from '../../services/bungieIconService';

// Define proper types
interface GroupItem {
  id: number;
  name: string;
  display_name?: string;
}

interface CategoryItem {
  id: number;
  name: string;
  display_name?: string;
}

interface AssignStatus {
  type: 'success' | 'error' | 'loading';
  message: string;
}

interface AssignTarget {
  id: number;
  type: 'group' | 'category';
}

const IconManager: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [groupsWithoutIcons, setGroupsWithoutIcons] = useState<GroupItem[]>([]);
  const [categoriesWithoutIcons, setCategoriesWithoutIcons] = useState<CategoryItem[]>([]);
  const [availableIcons, setAvailableIcons] = useState<string[]>([]);
  const [selectedIcon, setSelectedIcon] = useState<string | null>(null);
  const [assigningIconTo, setAssigningIconTo] = useState<AssignTarget | null>(null);
  const [assignStatus, setAssignStatus] = useState<AssignStatus | null>(null);
  const [activeTab, setActiveTab] = useState<'groups' | 'categories'>('groups');
  const [useInlineIcons, setUseInlineIcons] = useState<boolean>(true);

  // Default fallback icons built into our component
  const defaultIconPaths = [
    '/common/destiny2_content/icons/fc5630b950263a1def7f51bd7fe82d34.png', // Generic
    '/common/destiny2_content/icons/DestinyActivityModeDefinition_bfe80e3dafe6686a9dc42df0606bdc9b.png', // Raid
    '/common/destiny2_content/icons/DestinyActivityModeDefinition_c5100a377a1b1dbb244598e24020b4f3.png', // Crucible
    '/common/destiny2_content/icons/DestinyActivityModeDefinition_e35a3ca541706b06e0d1373e23b83032.png', // Trials
    '/common/destiny2_content/icons/fc96c345e8c9c5f03237157bb5e9a756.png', // Destinations
    '/common/destiny2_content/icons/a5108048fdf62c28588f9527eb7d93cd.png', // Gambit
    '/common/destiny2_content/icons/f15e21cf3c0fe6b0c53a4a297bc908fb.png', // Dungeon
    '/common/destiny2_content/icons/5a5d9b5c0221967f2d350ac13936c0df.png', // Triumphs
  ];

  useEffect(() => {
    fetchMissingIcons();
    fetchAvailableIcons();
  }, []);

  const fetchMissingIcons = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      // Create mock data if the API isn't available
      let mockGroups: GroupItem[] = [
        { id: 1, name: 'triumphs', display_name: 'Triumphs' },
        { id: 2, name: 'raids', display_name: 'Raids' },
        { id: 3, name: 'crucible', display_name: 'Crucible' }
      ];

      let mockCategories: CategoryItem[] = [
        { id: 1, name: 'vanguard', display_name: 'Vanguard' },
        { id: 2, name: 'gambit', display_name: 'Gambit' },
        { id: 3, name: 'seasonal', display_name: 'Seasonal' }
      ];

      try {
        const accessToken = getAccessToken();
        const response = await axios.get(`${API_CONFIG.BASE_URL}/admin/missing-icons`, {
          headers: {
            Authorization: `Bearer ${accessToken || ''}`
          }
        });
        
        if (response.data && response.data.status === 'success') {
          setGroupsWithoutIcons(response.data.data.groups || []);
          setCategoriesWithoutIcons(response.data.data.categories || []);
        } else {
          console.warn('API returned unexpected format, using mock data');
          setGroupsWithoutIcons(mockGroups);
          setCategoriesWithoutIcons(mockCategories);
        }
      } catch (apiError) {
        console.warn('API request failed, using mock data', apiError);
        setGroupsWithoutIcons(mockGroups);
        setCategoriesWithoutIcons(mockCategories);
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred while fetching missing icons';
      setError(errorMessage);
      console.error('Error in fetchMissingIcons:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableIcons = async (): Promise<void> => {
    try {
      try {
        const accessToken = getAccessToken();
        const response = await axios.get(`${API_CONFIG.BASE_URL}/admin/available-icons`, {
          headers: {
            Authorization: `Bearer ${accessToken || ''}`
          }
        });
        
        if (response.data && response.data.status === 'success') {
          setAvailableIcons(response.data.data || []);
        } else {
          console.warn('API returned unexpected format, using default icons');
          setAvailableIcons(defaultIconPaths);
        }
      } catch (apiError) {
        console.warn('API request failed, using default icons', apiError);
        setAvailableIcons(defaultIconPaths);
      }
    } catch (error: unknown) {
      console.error('Error in fetchAvailableIcons:', error);
      setAvailableIcons(defaultIconPaths);
    }
  };

  const assignIcon = async (itemId: number, itemType: 'group' | 'category'): Promise<void> => {
    if (!selectedIcon) {
      setAssignStatus({
        type: 'error',
        message: 'No icon selected. Please select an icon first.'
      });
      return;
    }

    try {
      setAssignStatus({
        type: 'loading',
        message: `Assigning icon to ${itemType} ${itemId}...`
      });

      try {
        const accessToken = getAccessToken();
        const response = await axios.post(`${API_CONFIG.BASE_URL}/admin/assign-icon`, {
          id: itemId,
          type: itemType,
          icon_url: selectedIcon
        }, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken || ''}`
          }
        });
        
        if (response.data && response.data.status === 'success') {
          setAssignStatus({
            type: 'success',
            message: `Icon successfully assigned to ${itemType} ${itemId}`
          });
          
          // Remove the item from the list
          if (itemType === 'group') {
            setGroupsWithoutIcons(groups => groups.filter(g => g.id !== itemId));
          } else {
            setCategoriesWithoutIcons(cats => cats.filter(c => c.id !== itemId));
          }
          
          // Clear the selection after successful assignment
          setAssigningIconTo(null);
        } else {
          throw new Error(response.data?.message || `Failed to assign icon to ${itemType}`);
        }
      } catch (apiError: any) {
        // If the API isn't available, simulate success for development
        if (process.env.NODE_ENV === 'development') {
          console.warn('API request failed, simulating success for development', apiError);
          
          setAssignStatus({
            type: 'success',
            message: `Icon successfully assigned to ${itemType} ${itemId} (simulated)`
          });
          
          // Remove the item from the list
          if (itemType === 'group') {
            setGroupsWithoutIcons(groups => groups.filter(g => g.id !== itemId));
          } else {
            setCategoriesWithoutIcons(cats => cats.filter(c => c.id !== itemId));
          }
          
          // Clear the selection
          setAssigningIconTo(null);
        } else {
          throw apiError;
        }
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred while assigning the icon';
      setAssignStatus({
        type: 'error',
        message: errorMessage
      });
      console.error('Error assigning icon:', error);
    }
  };

  // Helper to generate URL for viewing a group or category
  const getViewUrl = (item: GroupItem | CategoryItem, type: 'group' | 'category'): string => {
    // For groups, we link to the achievements page with the group parameter
    if (type === 'group') {
      return `/achievements?group=${item.id}`;
    }
    // For categories, we need both group and category parameters
    // Since we don't know the parent group, we just send the category ID
    return `/achievements?category=${item.id}`;
  };

  // Render the icon selection grid
  const renderIconsGrid = (): React.ReactNode => {
    // If we're using inline icons (SVG fallbacks), we render those directly
    if (useInlineIcons) {
      return (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 max-h-48 overflow-y-auto p-2 bg-gray-700 rounded-lg">
          {Object.entries(FALLBACK_ICONS).map(([key, value]) => (
            <div 
              key={key}
              onClick={() => {
                // Just use the key as the icon path - our updated service will handle it
                setSelectedIcon(`/${key.toLowerCase()}`);
              }}
              className={`p-2 border rounded-md cursor-pointer hover:bg-gray-600 ${
                selectedIcon === `/${key.toLowerCase()}` ? 'border-destiny-gold bg-gray-600' : 'border-gray-600'
              }`}
            >
              <img 
                src={value}
                alt={`Icon ${key}`}
                className="h-10 w-10 mx-auto"
              />
            </div>
          ))}
        </div>
      );
    }
    
    // Otherwise, we use the API-provided icons
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 max-h-48 overflow-y-auto p-2 bg-gray-700 rounded-lg">
        {availableIcons.map((icon, index) => (
          <div 
            key={index}
            onClick={() => setSelectedIcon(icon)}
            className={`p-2 border rounded-md cursor-pointer hover:bg-gray-600 ${
              selectedIcon === icon ? 'border-destiny-gold bg-gray-600' : 'border-gray-600'
            }`}
          >
            <img 
              src={getBungieIconUrl(icon)}
              alt={`Icon ${index}`}
              className="h-10 w-10 mx-auto"
              onError={(e) => handleIconError(e, icon)}
            />
          </div>
        ))}
        {availableIcons.length === 0 && (
          <div className="col-span-full text-center text-gray-400 py-4">
            No icons available. Try caching some icons first.
          </div>
        )}
      </div>
    );
  };

  const renderItemsList = (items: GroupItem[] | CategoryItem[], type: 'group' | 'category'): React.ReactNode => {
    return (
      <div className="space-y-4 max-h-96 overflow-y-auto">
        {items.length === 0 ? (
          <div className="text-center text-gray-400 py-8">
            All {type}s have icons assigned 🎉
          </div>
        ) : (
          items.map(item => (
            <div key={item.id} className="bg-gray-700 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-10 h-10 mr-3 bg-gray-600 rounded flex items-center justify-center">
                    <img 
                      src={createTextIconPlaceholder(item.display_name || item.name)}
                      alt={item.display_name || item.name}
                      className="w-full h-full rounded"
                    />
                  </div>
                  <div>
                    <h3 className="text-white font-medium">{item.display_name || item.name}</h3>
                    <p className="text-sm text-gray-400">ID: {item.id}</p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  {/* View button - opens the category/group in a new tab */}
                  <a
                    href={getViewUrl(item, type)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gray-600 hover:bg-gray-500 text-white px-3 py-1 rounded text-sm flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    View
                  </a>
                  
                  {/* Assign icon button */}
                  <button
                    onClick={() => setAssigningIconTo({ id: item.id, type })}
                    className="bg-destiny-blue hover:bg-opacity-90 text-white px-3 py-1 rounded text-sm"
                  >
                    Assign Icon
                  </button>
                </div>
              </div>
              
              {assigningIconTo && assigningIconTo.id === item.id && assigningIconTo.type === type && (
                <div className="mt-4 border-t border-gray-600 pt-4">
                  <h4 className="text-destiny-gold font-medium mb-2">Select an icon to assign:</h4>
                  
                  <div className="mb-3 flex items-center">
                    <span className="mr-3 text-sm text-gray-400">Icon source:</span>
                    <button
                      onClick={() => setUseInlineIcons(true)}
                      className={`px-3 py-1 rounded-l text-sm ${
                        useInlineIcons ? 'bg-destiny-blue text-white' : 'bg-gray-600 text-gray-300'
                      }`}
                    >
                      Built-in
                    </button>
                    <button
                      onClick={() => setUseInlineIcons(false)}
                      className={`px-3 py-1 rounded-r text-sm ${
                        !useInlineIcons ? 'bg-destiny-blue text-white' : 'bg-gray-600 text-gray-300'
                      }`}
                    >
                      Bungie API
                    </button>
                  </div>
                  
                  {renderIconsGrid()}
                  
                  <div className="mt-4 flex space-x-3">
                    <button
                      onClick={() => assignIcon(item.id, type)}
                      disabled={!selectedIcon}
                      className={`bg-destiny-green text-white px-3 py-1 rounded text-sm ${
                        !selectedIcon ? 'opacity-50 cursor-not-allowed' : 'hover:bg-opacity-90'
                      }`}
                    >
                      Confirm Assignment
                    </button>
                    <button
                      onClick={() => setAssigningIconTo(null)}
                      className="bg-gray-600 hover:bg-gray-500 text-white px-3 py-1 rounded text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                  
                  {selectedIcon && (
                    <div className="mt-3 flex items-center">
                      <span className="text-gray-400 text-sm mr-2">Selected icon:</span>
                      <img 
                        src={getBungieIconUrl(selectedIcon)}
                        alt="Selected icon" 
                        className="h-6 w-6"
                        onError={(e) => handleIconError(e, selectedIcon)}
                      />
                      <span className="text-gray-400 text-sm ml-2 truncate max-w-xs">{selectedIcon}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    );
  };

  const handleRunCachingTool = () => {
    setAssignStatus({
      type: 'loading',
      message: 'Caching Bungie icons... This may take a moment.'
    });

    // Attempt to call the cache-icons endpoint
    const accessToken = getAccessToken();
    axios.post(`${API_CONFIG.BASE_URL}/admin/cache-icons`, {}, {
      headers: {
        Authorization: `Bearer ${accessToken || ''}`
      }
    }).then(response => {
      if (response.data && response.data.status === 'success') {
        setAssignStatus({
          type: 'success',
          message: `Icon caching completed: ${response.data.data.cached} icons cached`
        });
        // Refresh available icons
        fetchAvailableIcons();
      } else {
        throw new Error(response.data?.message || 'Failed to cache icons');
      }
    }).catch(error => {
      console.error('Icon caching error:', error);
      setAssignStatus({
        type: 'error',
        message: 'Error caching icons: ' + (error.response?.data?.message || error.message)
      });
    });
  };

  const handleUpdateMissingIcons = () => {
    setAssignStatus({
      type: 'loading',
      message: 'Updating missing icons automatically...'
    });

    // Attempt to call the update-icons endpoint
    const accessToken = getAccessToken();
    axios.post(`${API_CONFIG.BASE_URL}/admin/update-icons`, {}, {
      headers: {
        Authorization: `Bearer ${accessToken || ''}`
      }
    }).then(response => {
      if (response.data && response.data.status === 'success') {
        setAssignStatus({
          type: 'success',
          message: `Icons updated: ${response.data.data.groups_updated} groups, ${response.data.data.categories_updated} categories`
        });
        // Refresh lists
        fetchMissingIcons();
      } else {
        throw new Error(response.data?.message || 'Failed to update icons');
      }
    }).catch(error => {
      console.error('Icon update error:', error);
      setAssignStatus({
        type: 'error',
        message: 'Error updating icons: ' + (error.response?.data?.message || error.message)
      });
      
      // In development mode, allow simulated success even on API error
      if (process.env.NODE_ENV === 'development') {
        setTimeout(() => {
          setAssignStatus({
            type: 'success',
            message: 'Development mode: Simulated successful icon update'
          });
          fetchMissingIcons();
        }, 1500);
      }
    });
  };

  return (
    <div className="bg-gray-800 rounded-lg p-6">
      <h2 className="text-xl font-bold text-destiny-gold mb-4">Icon Manager</h2>
      
      {error && (
        <div className="bg-destiny-red bg-opacity-20 border border-destiny-red rounded p-4 mb-4">
          <p className="text-destiny-red">{error}</p>
          <button 
            onClick={() => setError(null)} 
            className="text-sm text-destiny-red hover:underline mt-2"
          >
            Dismiss
          </button>
        </div>
      )}
      
      {assignStatus && (
        <div className={`mb-4 p-4 rounded border ${
          assignStatus.type === 'success' 
            ? 'bg-destiny-green bg-opacity-20 border-destiny-green text-destiny-green' 
            : assignStatus.type === 'error'
              ? 'bg-destiny-red bg-opacity-20 border-destiny-red text-destiny-red'
              : 'bg-gray-700 border-gray-600 text-white'
        }`}>
          <p className="flex items-center">
            {assignStatus.type === 'loading' && (
              <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {assignStatus.message}
          </p>
          {assignStatus.type !== 'loading' && (
            <button 
              onClick={() => setAssignStatus(null)} 
              className="text-sm hover:underline mt-1"
            >
              Dismiss
            </button>
          )}
        </div>
      )}
      
      <div className="mb-6">
        <div className="border-b border-gray-700">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('groups')}
              className={`py-2 px-1 text-sm font-medium ${
                activeTab === 'groups'
                  ? 'border-b-2 border-destiny-gold text-destiny-gold'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Groups without Icons ({groupsWithoutIcons.length})
            </button>
            <button
              onClick={() => setActiveTab('categories')}
              className={`py-2 px-1 text-sm font-medium ${
                activeTab === 'categories'
                  ? 'border-b-2 border-destiny-gold text-destiny-gold'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Categories without Icons ({categoriesWithoutIcons.length})
            </button>
          </nav>
        </div>
      </div>
      
      {loading ? (
        <div className="py-12 flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-destiny-gold"></div>
        </div>
      ) : (
        <>
          {activeTab === 'groups' && renderItemsList(groupsWithoutIcons, 'group')}
          {activeTab === 'categories' && renderItemsList(categoriesWithoutIcons, 'category')}
        </>
      )}
      
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <button
          onClick={fetchMissingIcons}
          className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded"
        >
          Refresh Lists
        </button>
        <button
          onClick={handleRunCachingTool}
          className="bg-destiny-blue hover:bg-opacity-90 text-white px-4 py-2 rounded"
        >
          Cache More Icons
        </button>
        <button
          onClick={handleUpdateMissingIcons}
          className="col-span-1 sm:col-span-2 bg-destiny-gold hover:bg-opacity-90 text-destiny-dark font-medium px-4 py-2 rounded"
        >
          Auto-Assign Default Icons
        </button>
      </div>
    </div>
  );
};

export default IconManager;